import Swiper from "swiper";
import { EffectFade, Navigation, Pagination } from "swiper/modules";
import gsap from "gsap";

let activeTween = null;
let exitTween = null;

const EASE_IN = "power3.out";
const EASE_OUT = "power2.inOut";

function getSlideElements(slide) {
  if (!slide) return null;

  const title = slide.querySelector(".cocktail-title");
  const desc = slide.querySelector(".cocktail-description");
  const details = slide.querySelector(".cocktail-details");
  const detailBlocks = [...slide.querySelectorAll(".cocktail-details__block")];
  const sectionTitles = [
    ...slide.querySelectorAll(".cocktail-ingredients-title"),
  ];
  const ingredients = [
    ...slide.querySelectorAll(".cocktail-ingredients-list li"),
  ];
  const image = slide.querySelector(".cocktail-image");
  const targets = [
    title,
    image,
    desc,
    details,
    ...detailBlocks,
    ...sectionTitles,
    ...ingredients,
  ].filter(Boolean);

  return {
    title,
    image,
    desc,
    details,
    detailBlocks,
    sectionTitles,
    ingredients,
    targets,
  };
}

function setSlideStartState(slide) {
  const els = getSlideElements(slide);
  if (!els) return;

  gsap.killTweensOf(els.targets);
  gsap.set(els.title, { y: -12, opacity: 0 });
  gsap.set(els.image, { y: 14, opacity: 0, scale: 0.97 });
  gsap.set(els.desc, { x: -14, opacity: 0 });

  if (els.details) {
    gsap.set(els.details, { x: 14, opacity: 0 });
    gsap.set(els.detailBlocks, { y: 10, opacity: 0 });
    gsap.set(els.sectionTitles, { y: 8, opacity: 0 });
    gsap.set(els.ingredients, { x: 10, opacity: 0 });
    return;
  }

  gsap.set(els.ingredients, { x: 14, opacity: 0 });
}

function animateSlideOut(slide) {
  const els = getSlideElements(slide);
  if (!els) return;

  if (exitTween) exitTween.kill();
  gsap.killTweensOf(els.targets);

  exitTween = gsap
    .timeline()
    .to(els.ingredients, {
      x: 10,
      opacity: 0,
      duration: 0.32,
      stagger: 0.03,
      ease: EASE_OUT,
    })
    .to(
      els.sectionTitles,
      { y: 6, opacity: 0, duration: 0.28, ease: EASE_OUT },
      0,
    )
    .to(
      els.detailBlocks,
      { y: 8, opacity: 0, duration: 0.32, ease: EASE_OUT },
      0,
    )
    .to(els.details, { x: 10, opacity: 0, duration: 0.32, ease: EASE_OUT }, 0)
    .to(els.desc, { x: -10, opacity: 0, duration: 0.35, ease: EASE_OUT }, 0)
    .to(
      els.image,
      { y: -10, opacity: 0, scale: 0.98, duration: 0.55, ease: EASE_OUT },
      0.05,
    )
    .to(els.title, { y: -10, opacity: 0, duration: 0.5, ease: EASE_OUT }, 0.1);
}

function animateDetailContent(els, timeline, position) {
  timeline.to(
    els.desc,
    { x: 0, opacity: 1, duration: 0.35, ease: EASE_IN },
    position,
  );

  if (!els.details) {
    timeline.to(
      els.ingredients,
      {
        x: 0,
        opacity: 1,
        duration: 0.28,
        stagger: 0.04,
        ease: EASE_IN,
      },
      "-=0.18",
    );
    return;
  }

  timeline
    .to(
      els.details,
      { x: 0, opacity: 1, duration: 0.35, ease: EASE_IN },
      "-=0.15",
    )
    .to(
      els.detailBlocks,
      { y: 0, opacity: 1, duration: 0.3, stagger: 0.06, ease: EASE_IN },
      "-=0.22",
    )
    .to(
      els.sectionTitles,
      { y: 0, opacity: 1, duration: 0.28, stagger: 0.05, ease: EASE_IN },
      "-=0.24",
    )
    .to(
      els.ingredients,
      {
        x: 0,
        opacity: 1,
        duration: 0.25,
        stagger: 0.04,
        ease: EASE_IN,
      },
      "-=0.22",
    );
}

function animateCocktailSlide(slide) {
  const els = getSlideElements(slide);
  if (!els) return;

  if (activeTween) activeTween.kill();
  setSlideStartState(slide);

  activeTween = gsap.timeline({ delay: 0.1 });
  activeTween.to(els.title, {
    y: 0,
    opacity: 1,
    duration: 0.55,
    ease: EASE_IN,
  });
  activeTween.to(
    els.image,
    { y: 0, opacity: 1, scale: 1, duration: 0.6, ease: EASE_IN },
    "-=0.1",
  );
  animateDetailContent(els, activeTween, "-=0.22");
}

function syncMenuSwiperHeight(root, swiper) {
  if (!document.body.classList.contains("menu-page")) return;

  const section = root.closest(".menu-page-section");
  if (!section) return;

  const height = Math.round(section.getBoundingClientRect().height);
  root.style.height = `${height}px`;

  if (swiper && typeof swiper.update === "function") {
    swiper.update();
  }
}

function bindMenuSwiperHeight(root, swiper) {
  const run = () => syncMenuSwiperHeight(root, swiper);

  run();
  requestAnimationFrame(run);
  window.addEventListener("resize", run);
  window.addEventListener("orientationchange", () => {
    setTimeout(run, 150);
  });
}

function initDetailAnimation(root) {
  const slide = root.querySelector(".cocktail-slide");
  if (!slide) return;

  bindMenuSwiperHeight(root, null);
  root.classList.add("cocktail-swiper--ready");
  animateCocktailSlide(slide);
}

export function initCocktailSlider() {
  const root = document.querySelector(".cocktail-swiper");
  if (!root) return;

  const slides = root.querySelectorAll(".swiper-slide");
  const isSlider =
    slides.length > 1 ||
    root.querySelector(".cocktail-swiper-pagination") ||
    root.querySelector(".cocktail-swiper-prev");

  if (!isSlider) {
    initDetailAnimation(root);
    return;
  }

  syncMenuSwiperHeight(root, null);

  const swiper = new Swiper(root, {
    modules: [EffectFade, Navigation, Pagination],
    effect: "fade",
    fadeEffect: { crossFade: true },
    slidesPerView: 1,
    speed: 850,
    loop: slides.length > 1,
    grabCursor: true,
    observer: true,
    observeParents: true,
    pagination: {
      el: root.querySelector(".cocktail-swiper-pagination"),
      clickable: true,
    },
    navigation: {
      nextEl: root.querySelector(".cocktail-swiper-next"),
      prevEl: root.querySelector(".cocktail-swiper-prev"),
    },
  });

  const playSlideTransition = () => {
    const prevSlide = swiper.slides[swiper.previousIndex];
    const nextSlide = swiper.slides[swiper.activeIndex];

    if (prevSlide && prevSlide !== nextSlide) {
      animateSlideOut(prevSlide);
    }

    animateCocktailSlide(nextSlide);
  };

  root.classList.add("cocktail-swiper--ready");
  bindMenuSwiperHeight(root, swiper);
  animateCocktailSlide(swiper.slides[swiper.activeIndex]);

  swiper.on("slideChangeTransitionStart", playSlideTransition);
}
